import React, { useState, useEffect, useCallback } from "react";
import ContentLayout from "@cloudscape-design/components/content-layout";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import Alert from "@cloudscape-design/components/alert";
import Flashbar from "@cloudscape-design/components/flashbar";
import HelpPanel from "@cloudscape-design/components/help-panel";
import Link from "@cloudscape-design/components/link";
import Box from "@cloudscape-design/components/box";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import FeedsList from "../components/FeedsList";
import CreateFeedModal from "../components/CreateFeedModal";

export default function FeedsPage({ setToolsOpen, setToolsContent, isAdmin, adminViewMode }) {
  const { t } = useTranslation();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [isPrimaryAdmin, setIsPrimaryAdmin] = useState(false);
  const [flashbarItems, setFlashbarItems] = useState([]);

  // Determine effective view mode
  const showAdminView = isAdmin && adminViewMode;

  // Load feeds function - memoized with useCallback
  const loadFeeds = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // In admin view mode, fetch all feeds; otherwise fetch only user's feeds
      const result = showAdminView ? await api.getAllFeeds() : await api.getFeeds();
      setFeeds(result.feeds || []);
      setIsPrimaryAdmin(result.is_primary_admin || false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [showAdminView]);

  // Reload feeds when view mode changes
  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  useEffect(() => {
    // Set up help panel content
    setToolsContent(
      <HelpPanel header={<h2>{t("help.main.title")}</h2>}>
        <SpaceBetween size="m">
          <Box>
            <h3>{t("help.section.getting-started")}</h3>
            <p>{t("help.main.intro")}</p>
          </Box>

          <Box>
            <h3>{t("help.section.creating-feed")}</h3>
            <ol>
              <li>{t("help.step.step1")}</li>
              <li>{t("help.step.step2")}</li>
              <li>
                {t("help.step.step3")}
                <ul>
                  <li>
                    <strong>{t("help.step.step3a")}</strong> {t("help.step.step3a-desc")}
                  </li>
                  <li>
                    <strong>{t("help.step.step3b")}</strong> {t("help.step.step3b-desc")}
                  </li>
                </ul>
              </li>
              <li>{t("help.step.step4")}</li>
              <li>{t("help.step.step5")}</li>
              <li>{t("help.step.step6")}</li>
            </ol>

            <h4>{t("help.section.filter-examples")}</h4>

            <Box margin={{ left: "m" }}>
              <h5>{t("help.example.or-logic")}</h5>
              <p>
                {t("help.example.or-example")}
                <br />
                {t("help.example.or-example-services")}
                <br />
                <em>{t("help.example.or-example-result")}</em>
              </p>

              <h5>{t("help.example.and-logic")}</h5>
              <p>
                {t("help.example.and-example")}
                <br />
                {t("help.example.and-example-services")}
                <br />
                {t("help.example.and-example-keywords")}
                <br />
                <em>{t("help.example.and-example-result")}</em>
              </p>
            </Box>
          </Box>

          <Box>
            <h3>{t("help.section.rss-access")}</h3>
            <p>{t("help.text.rss-access-desc")}</p>
          </Box>

          <Box>
            <h3>{t("help.section.slack-notifications")}</h3>
            <p>{t("help.text.slack-intro")}</p>

            <h4>{t("help.slack.step1")}</h4>
            <ol>
              <li>{t("help.slack.step1a")}</li>
              <li>{t("help.slack.step1b")}</li>
              <li>
                {t("help.slack.step1c")} <code>/invite @relevant_news_updates</code>
              </li>
            </ol>

            <h4>{t("help.slack.step2")}</h4>
            <ol>
              <li>{t("help.slack.step2a")}</li>
              <li>{t("help.slack.step2b")}</li>
              <li>{t("help.slack.step2c")}</li>
            </ol>

            <h4>{t("help.slack.step3")}</h4>
            <ol>
              <li>{t("help.slack.step3a")}</li>
              <li>{t("help.slack.step3b")}</li>
            </ol>

            <Box variant="p">
              <strong>{t("help.slack.alternative")}</strong> {t("help.slack.alternative-desc")}
            </Box>
          </Box>

          <Box>
            <h3>{t("help.section.managing-feeds")}</h3>
            <p>{t("help.manage.select")}</p>
            <ul>
              <li>
                <strong>{t("help.manage.edit")}</strong> {t("help.manage.edit-desc")}
              </li>
              <li>
                <strong>{t("help.manage.enable")}</strong> {t("help.manage.enable-desc")}
              </li>
              <li>
                <strong>{t("help.manage.regenerate")}</strong> {t("help.manage.regenerate-desc")}
              </li>
              <li>
                <strong>{t("help.manage.test-slack")}</strong> {t("help.manage.test-slack-desc")}
              </li>
              <li>
                <strong>{t("help.manage.delete")}</strong> {t("help.manage.delete-desc")}
              </li>
            </ul>
          </Box>
        </SpaceBetween>
      </HelpPanel>,
    );
  }, [setToolsContent, t]);

  const handleCreateFeed = () => {
    setEditingFeed(null);
    setShowModal(true);
  };

  const handleEditFeed = (feed) => {
    setEditingFeed(feed);
    setShowModal(true);
  };

  const handleSubmitFeed = async (feedData) => {
    try {
      if (editingFeed) {
        await api.updateFeed(editingFeed.feed_id, feedData);
      } else {
        await api.createFeed(feedData);
      }
      setShowModal(false);
      setEditingFeed(null);
      loadFeeds();
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteFeed = async (feedId) => {
    try {
      await api.deleteFeed(feedId);
      loadFeeds();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRegenerateRss = async (feedId) => {
    try {
      await api.regenerateRss(feedId);
      // Show success message or refresh feeds
      loadFeeds();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTestSlack = async (feedId) => {
    try {
      const result = await api.testSlackWebhook(feedId);
      setError(""); // Clear any previous errors
      setFlashbarItems([
        {
          type: "success",
          content: result.message || "Test message sent successfully!",
          dismissible: true,
          onDismiss: () => setFlashbarItems([]),
          id: "test-slack-success",
        },
      ]);
    } catch (err) {
      setFlashbarItems([
        {
          type: "error",
          content: err.message || "Failed to send test message",
          dismissible: true,
          onDismiss: () => setFlashbarItems([]),
          id: "test-slack-error",
        },
      ]);
    }
  };

  const handleToggleEnabled = async (feedId, enabled) => {
    try {
      await api.updateFeed(feedId, { enabled });
      loadFeeds();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={showAdminView ? t("feeds.page.description-admin") : t("feeds.page.description-user")}
          info={
            <Link variant="info" onFollow={() => setToolsOpen(true)}>
              {t("help.action.info")}
            </Link>
          }
          actions={
            <Button variant="primary" onClick={handleCreateFeed}>
              {t("feeds.action.create-feed")}
            </Button>
          }
        >
          {showAdminView ? t("feeds.page.title-admin") : t("feeds.page.title-user")}
        </Header>
      }
    >
      <Flashbar items={flashbarItems} />
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError("")}>
          {error}
        </Alert>
      )}

      <FeedsList
        key={showAdminView ? "admin" : "user"}
        feeds={feeds}
        loading={loading}
        onEdit={handleEditFeed}
        onDelete={handleDeleteFeed}
        onRefresh={loadFeeds}
        onRegenerateRss={handleRegenerateRss}
        onTestSlack={handleTestSlack}
        onToggleEnabled={handleToggleEnabled}
        isPrimaryAdmin={showAdminView}
        onShowError={(message) =>
          setFlashbarItems([
            {
              type: "error",
              content: message,
              dismissible: true,
              onDismiss: () => setFlashbarItems([]),
              id: "feed-error",
            },
          ])
        }
      />

      {showModal && (
        <CreateFeedModal
          visible={showModal}
          onDismiss={() => {
            setShowModal(false);
            setEditingFeed(null);
          }}
          onSubmit={handleSubmitFeed}
          editFeed={editingFeed}
        />
      )}
    </ContentLayout>
  );
}
